import { Component, OnInit, OnChanges, Input } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { TranslocoModule } from '@jsverse/transloco';
import { TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { TranslationService } from 'src/app/services/translation.service';
import { QaiTableComponent, TableRow, HeaderConfig, QaiItemGridComponent, QaiLoadingComponent, SortDirection } from '@qai/ui';
import { IUserAssignmentMetricsResponse } from 'src/app/interfaces/iuser-assignment-metrics-response';
import { IUserAssignmentResponse } from 'src/app/interfaces/iuser-assignment-response';
import { CommonModule } from '@angular/common';
import { LocaleService } from 'src/app/services/locale.service';
import { formatHoursMinutesSeconds } from 'src/app/utils/date-parser.util';
import { localeLowerCase } from '../../../../../../../utils/locale-case.util';

@Component({
  selector: 'app-certification-tab',
  templateUrl: './certification-tab.component.html',
  styleUrls: ['./certification-tab.component.scss'],
  standalone: true,
  imports: [CommonModule, TranslocoModule, QaiTableComponent, QaiItemGridComponent, QaiLoadingComponent],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'reportingV2',
    }
  ],
})
export class CertificationTabComponent implements OnInit, OnChanges {
  @Input() assignments: IUserAssignmentResponse[] = [];
  @Input() assignmentMetrics: IUserAssignmentMetricsResponse | null = null;
  @Input() isLoading: boolean = false;
  
  certifiedOnFirstTryPercentage: number = 0;
  averageCertificationScore: number = 0;
  totalCertificationTime: string = '0';

  isTranslationLoaded = false;
  currentPage = 1;
  
  headers: HeaderConfig[] = [];
  tableData: TableRow[] = [];
  
  initialSortColumn: string = '';
  initialSortDirection: SortDirection = 'desc';
  
  // Future date used for sorting null certification dates
  private readonly FUTURE_DATE_FOR_NULL = '9999-12-31T23:59:59.999Z';

  constructor(
    private readonly translocoService: TranslocoService,
    private readonly translationService: TranslationService,
    protected localeService: LocaleService
  ) {}

  ngOnInit(): void {
    this.translationService.loadScopedTranslation('reportingV2')
      .then(() => {
        this.isTranslationLoaded = true;
        this.initializeTable();
        this.updateTableData();
        this.updateMetrics();
      })
      .catch((error) => {
        this.isTranslationLoaded = false;
      });
  }

  ngOnChanges(): void {
    if (this.isTranslationLoaded) {
      this.updateTableData();
    }
    this.updateMetrics();
  }

  private initializeTable() {
    this.headers = [
      {
        name: this.translocoService.translate('ASSIGNMENT_NAME', {}, 'reportingV2'),
        sortable: true,
        columnWidth: '30%'
      },
      {
        name: this.translocoService.translate('STATUS', {}, 'reportingV2'),
        sortable: true,
        columnWidth: '20%'
      },
      {
        name: this.translocoService.translate('CERTIFIED_ON_FIRST_TRY', {}, 'reportingV2'),
        sortable: true,
        columnWidth: '25%'
      },
      {
        name: this.translocoService.translate('DATE_OF_CERTIFICATION', {}, 'reportingV2'),
        sortable: true,
        columnWidth: '25%'
      }
    ];

    this.initialSortColumn = this.translocoService.translate('DATE_OF_CERTIFICATION', {}, 'reportingV2');
    this.initialSortDirection = 'desc';

    this.tableData = this.createTableRows(this.assignments || []);
  }

  private updateTableData(): void {
    this.tableData = this.createTableRows(this.assignments || []);
  }

  private updateMetrics(): void {
    if (this.assignmentMetrics) {
      this.certifiedOnFirstTryPercentage = this.assignmentMetrics.percentCertifiedFirstTry ?? 0;
      this.averageCertificationScore = this.assignmentMetrics.avgCertificationScore ?? 0;
      const totalSeconds = this.assignmentMetrics.totalTimeCertifyingSeconds ?? 0;
      this.totalCertificationTime = this.translateHoursDuration(formatHoursMinutesSeconds(totalSeconds));
    }
  }

  private translateHoursDuration(durationString: string): string {
    const hoursLabel = this.translocoService.translate('HOURS_LABEL', {}, 'reportingV2');
    const minText = this.translocoService.translate('MIN', {}, 'reportingV2');
    const secText = this.translocoService.translate('SEC', {}, 'reportingV2');
    return this.localeService.formatDurationSpacing(
      durationString.replace('hr', hoursLabel).replace('min', minText).replace('sec', secText)
    );
  }

  private mapStatusToLabel(status: string): string {
    // SAFE: backend status enum value
    switch ((status || '').toUpperCase()) {
      case 'COMPLETED':
        return this.translocoService.translate('CERTIFIED_LABEL', {}, 'reportingV2');
      case 'IN_PROGRESS':
        return this.translocoService.translate('IN_PROGRESS_LABEL', {}, 'reportingV2');
      case 'OUT_OF_ATTEMPTS':
        return this.translocoService.translate('OUT_OF_ATTEMPTS_LABEL', {}, 'reportingV2');
      case 'NOT_STARTED':
        return this.translocoService.translate('ASSIGNED_LABEL', {}, 'reportingV2');
      default:
        return '';
    }
  }

  private createTableRows(assignments: IUserAssignmentResponse[]): TableRow[] {
    return assignments.map(assignment => {
      const statusLabel = this.mapStatusToLabel(assignment.status);
      // SAFE: backend status enum field
      const statusRaw = (assignment.status || '').replace(/_/g, ' ').trim().toLowerCase();
      // REMEDIATE: lowercases a translated/localized UI label
      const outOfAttemptsLabel = localeLowerCase(
        this.translocoService
          .translate('OUT_OF_ATTEMPTS_LABEL', {}, 'reportingV2')
          .trim(),
        this.translocoService.getActiveLang() || 'en'
      );

      const isOutOfAttempts = statusRaw == outOfAttemptsLabel;
      const hasDateCertification = assignment.dateCertification && assignment.dateCertification.trim();
      const dateValue = hasDateCertification 
        ? assignment.dateCertification 
        : this.FUTURE_DATE_FOR_NULL;

      const dateDisplayValue = hasDateCertification 
        ? this.localeService.formatTableDate(assignment.dateCertification)
        : '-';
       
      const row: any = {
        id: assignment.assignmentUuid,
        [this.translocoService.translate('ASSIGNMENT_NAME', {}, 'reportingV2')]: { type: 'text', value: assignment.assignmentName },
        [this.translocoService.translate('STATUS', {}, 'reportingV2')]: { 
          type: 'text', 
          value: statusLabel,
          kind: isOutOfAttempts ? 'error' : undefined
        },
        [this.translocoService.translate('CERTIFIED_ON_FIRST_TRY', {}, 'reportingV2')]: { 
          type: 'text', 
          value: this.translocoService.translate(assignment.certifiedFirstTry ? 'YES_LABEL' : 'NO_LABEL', {}, 'reportingV2')
        },
        [this.translocoService.translate('DATE_OF_CERTIFICATION', {}, 'reportingV2')]: {
          type: 'text',
          value: dateDisplayValue,
          sortValue: dateValue  
        }
      };
      return row as TableRow;
    });
  }

  onPageChanged(event: any): void {
    this.currentPage = event.page;
  }
}
